/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import {
	type usePublicLinkPermissionsForObjectPublicLinkPermissionsForObjectQuery as usePublicLinkPermissionsForObjectPublicLinkPermissionsForObjectQuery$data,
	type usePublicLinkPermissionsForObjectPublicLinkPermissionsForObjectQueryVariables as usePublicLinkPermissionsForObjectPublicLinkPermissionsForObjectQuery$variables,
	PublicLinkPermissionsObjectType,
	PublicLinkPermissionsType,
} from './__types__/usePublicLinkPermissionsForObjectPublicLinkPermissionsForObjectQuery';

export type PublicLinkPermissionsForObjectData = {
	hasEditPermission: boolean;
	permissions: PublicLinkPermissionsType[];
};

export type PublicLinkPermissionsForObjectQueryData = {
	isLoading: boolean;
	refetch: () => void;
	error?: Error;
	data?: PublicLinkPermissionsForObjectData;
};

export const usePublicLinkPermissionsForObject = ({
	objectId,
	objectType = PublicLinkPermissionsObjectType.CONTENT,
	skip,
}: {
	objectId: string;
	objectType?: PublicLinkPermissionsObjectType;
	skip?: boolean;
}): PublicLinkPermissionsForObjectQueryData => {
	const { loading, error, data, refetch } = useQuery<
		usePublicLinkPermissionsForObjectPublicLinkPermissionsForObjectQuery$data,
		usePublicLinkPermissionsForObjectPublicLinkPermissionsForObjectQuery$variables
	>(
		graphql`
			query usePublicLinkPermissionsForObjectPublicLinkPermissionsForObjectQuery(
				$objectId: ID!
				$objectType: PublicLinkPermissionsObjectType!
			) {
				publicLinkPermissionsForObject(objectId: $objectId, objectType: $objectType) {
					permissions
				}
			}
		`,
		{
			variables: {
				objectId,
				objectType,
			},
			skip,
		},
	);

	useEffect(() => {
		if (isUnauthorizedError(error)) markErrorAsHandled(error);
	}, [error]);

	const hasEditPermission: boolean =
		data?.publicLinkPermissionsForObject?.permissions.includes(PublicLinkPermissionsType.EDIT) ??
		false;

	return loading
		? { isLoading: true, refetch }
		: {
				isLoading: false,
				error,
				refetch,
				data: error
					? undefined
					: {
							hasEditPermission,
							permissions: data?.publicLinkPermissionsForObject?.permissions ?? [],
						},
			};
};
